import { Ionicons } from '@expo/vector-icons';
import React, { useState, useEffect } from 'react';
import { View, Text, ScrollView, TouchableOpacity, Alert } from 'react-native';
import tw from 'twrnc';

import { t } from '../../../i18n/translations';
import { localizationAnalyticsService, LocalizationMetrics } from '../../localization/services/localizationAnalyticsService';
import { translationQualityService, TranslationQualityMetrics } from '../../localization/services/translationQualityService';
import { useLanguageStore } from '../../localization/stores/languageStore';

interface LocalizationDashboardProps {
  navigation: any;
}

export const LocalizationDashboard: React.FC<LocalizationDashboardProps> = ({ navigation }) => {
  const { currentLanguage } = useLanguageStore();
  const language = currentLanguage;
  const [activeTab, setActiveTab] = useState<'overview' | 'quality' | 'analytics' | 'community'>('overview');
  const [qualityMetrics, setQualityMetrics] = useState<Record<string, TranslationQualityMetrics[]>>({});
  const [analyticsMetrics, setAnalyticsMetrics] = useState<LocalizationMetrics[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedLanguage, setSelectedLanguage] = useState<string>('all');

  useEffect(() => {
    loadDashboardData();
  }, [selectedLanguage]);

  const loadDashboardData = async () => {
    setLoading(true);
    try {
      const [qualityData, analyticsData] = await Promise.all([
        translationQualityService.getAllLanguageQuality(),
        localizationAnalyticsService.getLocalizationMetrics()
      ]);

      setQualityMetrics(qualityData);
      setAnalyticsMetrics(analyticsData);
    } catch (error) {
      console.error('Failed to load dashboard data:', error);
      Alert.alert(t('admin.error', language), t('admin.failedToLoadDashboard', language));
    } finally {
      setLoading(false);
    }
  };

  const renderOverviewTab = () => {
    const totalLanguages = Object.keys(qualityMetrics).length;
    const totalUsers = analyticsMetrics.reduce((sum, m) => sum + m.totalUsers, 0);
    const nonEnglishUsers = analyticsMetrics
      .filter(m => m.language !== 'en')
      .reduce((sum, m) => sum + m.totalUsers, 0);
    const nonEnglishPercentage = totalUsers > 0 ? (nonEnglishUsers / totalUsers) * 100 : 0;

    return (
      <ScrollView style={tw`flex-1`}>
        {/* Key Metrics Cards */}
        <View style={tw`flex-row flex-wrap justify-between mb-6`}>
          <MetricCard
            title={t('admin.totalLanguages', language)}
            value={totalLanguages.toString()}
            icon="language"
            color="blue"
          />
          <MetricCard
            title={t('admin.totalUsers', language)}
            value={totalUsers.toLocaleString()}
            icon="people"
            color="green"
          />
          <MetricCard
            title={t('admin.nonEnglishUsage', language)}
            value={`${nonEnglishPercentage.toFixed(1)}%`}
            icon="globe"
            color="purple"
          />
          <MetricCard
            title={t('admin.avgQualityScore', language)}
            value="4.2"
            icon="star"
            color="yellow"
          />
        </View>

        {/* Language Status Grid */}
        <View style={tw`bg-white rounded-lg p-4 mb-6`}>
          <Text style={tw`text-lg font-bold text-gray-800 mb-4`}>
            {t('admin.overview', language)}
          </Text>
          <View style={tw`flex-row flex-wrap`}>
            {Object.entries(qualityMetrics).map(([language, metrics]) => (
              <LanguageStatusCard
                key={language}
                language={language}
                metrics={metrics}
                onPress={() => navigation.navigate('LanguageDetails', { language })}
              />
            ))}
          </View>
        </View>

        {/* Recent Activity */}
        <View style={tw`bg-white rounded-lg p-4`}>
          <Text style={tw`text-lg font-bold text-gray-800 mb-4`}>
            Recent Translation Activity
          </Text>
          <RecentActivityList />
        </View>
      </ScrollView>
    );
  };

  const renderQualityTab = () => (
    <ScrollView style={tw`flex-1`}>
      {/* Quality Overview */}
      <View style={tw`bg-white rounded-lg p-4 mb-4`}>
        <Text style={tw`text-lg font-bold text-gray-800 mb-4`}>
          Translation Quality Overview
        </Text>
        <QualityOverviewChart qualityMetrics={qualityMetrics} />
      </View>

      {/* Language Quality Details */}
      {Object.entries(qualityMetrics).map(([language, metrics]) => (
        <LanguageQualityCard
          key={language}
          language={language}
          metrics={metrics}
          onManage={() => navigation.navigate('ManageTranslations', { language })}
        />
      ))}
    </ScrollView>
  );

  const renderAnalyticsTab = () => (
    <ScrollView style={tw`flex-1`}>
      {/* Usage Analytics */}
      <View style={tw`bg-white rounded-lg p-4 mb-4`}>
        <Text style={tw`text-lg font-bold text-gray-800 mb-4`}>
          Language Usage Analytics
        </Text>
        <UsageAnalyticsChart metrics={analyticsMetrics} />
      </View>

      {/* Detailed Metrics */}
      {analyticsMetrics.map((metric) => (
        <AnalyticsMetricCard
          key={metric.language}
          metric={metric}
          onViewDetails={() => navigation.navigate('LanguageAnalytics', { language: metric.language })}
        />
      ))}
    </ScrollView>
  );

  const renderCommunityTab = () => (
    <ScrollView style={tw`flex-1`}>
      {/* Community Stats */}
      <View style={tw`bg-white rounded-lg p-4 mb-4`}>
        <Text style={tw`text-lg font-bold text-gray-800 mb-4`}>
          Community Translation Stats
        </Text>
        <CommunityStatsOverview />
      </View>

      {/* Pending Reviews */}
      <View style={tw`bg-white rounded-lg p-4 mb-4`}>
        <Text style={tw`text-lg font-bold text-gray-800 mb-4`}>
          Pending Reviews
        </Text>
        <PendingReviewsList />
      </View>

      {/* Top Contributors */}
      <View style={tw`bg-white rounded-lg p-4`}>
        <Text style={tw`text-lg font-bold text-gray-800 mb-4`}>
          Top Contributors
        </Text>
        <TopContributorsList />
      </View>
    </ScrollView>
  );

  return (
    <View style={tw`flex-1 bg-gray-50`}>
      {/* Header */}
      <View style={tw`bg-white p-4 border-b border-gray-200`}>
        <Text style={tw`text-2xl font-bold text-gray-800`}>
          {t('admin.localizationDashboard', language)}
        </Text>
        
        {/* Language Filter */}
        <View style={tw`flex-row items-center mt-2`}>
          <Text style={tw`text-gray-600 mr-2`}>{t('admin.filterByLanguage', language)}</Text>
          <TouchableOpacity
            style={tw`bg-blue-100 px-3 py-1 rounded-full`}
            onPress={() => {
              // Show language picker
            }}
          >
            <Text style={tw`text-blue-600 font-medium`}>
              {selectedLanguage === 'all' ? t('admin.allLanguages', language) : selectedLanguage.toUpperCase()}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Tab Navigation */}
      <View style={tw`bg-white border-b border-gray-200`}>
        <ScrollView horizontal showsHorizontalScrollIndicator={false}>
          <View style={tw`flex-row px-4`}>
            {[
              { key: 'overview', label: t('admin.overview', language), icon: 'grid' },
              { key: 'quality', label: t('admin.quality', language), icon: 'checkmark-circle' },
              { key: 'analytics', label: t('admin.analytics', language), icon: 'bar-chart' },
              { key: 'community', label: t('admin.community', language), icon: 'people' }
            ].map((tab) => (
              <TouchableOpacity
                key={tab.key}
                style={[
                  tw`flex-row items-center px-4 py-3 mr-2`,
                  activeTab === tab.key && tw`border-b-2 border-blue-500`
                ]}
                onPress={() => setActiveTab(tab.key as any)}
              >
                <Ionicons
                  name={tab.icon as any}
                  size={16}
                  color={activeTab === tab.key ? '#3b82f6' : '#6b7280'}
                />
                <Text style={[
                  tw`ml-2 font-medium`,
                  activeTab === tab.key ? tw`text-blue-600` : tw`text-gray-600`
                ]}>
                  {tab.label}
                </Text>
              </TouchableOpacity>
            ))}
          </View>
        </ScrollView>
      </View>

      {/* Tab Content */}
      <View style={tw`flex-1 p-4`}>
        {activeTab === 'overview' && renderOverviewTab()}
        {activeTab === 'quality' && renderQualityTab()}
        {activeTab === 'analytics' && renderAnalyticsTab()}
        {activeTab === 'community' && renderCommunityTab()}
      </View>
    </View>
  );
};

// Metric Card Component
interface MetricCardProps {
  title: string;
  value: string;
  icon: string;
  color: 'blue' | 'green' | 'purple' | 'yellow';
}

const MetricCard: React.FC<MetricCardProps> = ({ title, value, icon, color }) => {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    green: 'bg-green-100 text-green-600',
    purple: 'bg-purple-100 text-purple-600',
    yellow: 'bg-yellow-100 text-yellow-600'
  };

  return (
    <View style={tw`bg-white rounded-lg p-4 w-[48%] mb-3`}>
      <View style={tw`flex-row items-center justify-between mb-2`}>
        <View style={[tw`w-10 h-10 rounded-full items-center justify-center`, tw`${colorClasses[color].split(' ')[0]}`]}>
          <Ionicons name={icon as any} size={20} color={colorClasses[color].includes('blue') ? '#2563eb' : '#16a34a'} />
        </View>
      </View>
      <Text style={tw`text-2xl font-bold text-gray-800 mb-1`}>{value}</Text>
      <Text style={tw`text-sm text-gray-600`}>{title}</Text>
    </View>
  );
};

// Language Status Card Component
interface LanguageStatusCardProps {
  language: string;
  metrics: TranslationQualityMetrics[];
  onPress: () => void;
}

const LanguageStatusCard: React.FC<LanguageStatusCardProps> = ({ language, metrics, onPress }) => {
  const questionMetric = metrics.find(m => m.contentType === 'question');
  const completionPercentage = questionMetric?.completionPercentage || 0;
  const qualityScore = questionMetric?.avgQualityScore || 0;

  const getStatusColor = (percentage: number) => {
    if (percentage >= 90) return 'bg-green-100 border-green-300';
    if (percentage >= 70) return 'bg-yellow-100 border-yellow-300';
    return 'bg-red-100 border-red-300';
  };

  return (
    <TouchableOpacity
      style={[tw`w-[48%] p-3 rounded-lg border-2 mb-3`, tw`${getStatusColor(completionPercentage)}`]}
      onPress={onPress}
    >
      <Text style={tw`font-bold text-gray-800 mb-1`}>{language.toUpperCase()}</Text>
      <Text style={tw`text-sm text-gray-600 mb-2`}>
        {completionPercentage.toFixed(1)}% Complete
      </Text>
      <View style={tw`flex-row items-center`}>
        <Ionicons name="star" size={12} color="#fbbf24" />
        <Text style={tw`text-xs text-gray-600 ml-1`}>
          {qualityScore.toFixed(1)}
        </Text>
      </View>
    </TouchableOpacity>
  );
};

// Language Quality Card Component
interface LanguageQualityCardProps {
  language: string;
  metrics: TranslationQualityMetrics[];
  onManage: () => void;
}

const LanguageQualityCard: React.FC<LanguageQualityCardProps> = ({ language, metrics, onManage }) => {
  const questionMetric = metrics.find(m => m.contentType === 'question');
  
  return (
    <View style={tw`bg-white rounded-lg p-4 mb-4`}>
      <View style={tw`flex-row items-center justify-between mb-3`}>
        <Text style={tw`text-lg font-bold text-gray-800`}>
          {language.toUpperCase()}
        </Text>
        <TouchableOpacity
          style={tw`bg-blue-100 px-3 py-1 rounded-full`}
          onPress={onManage}
        >
          <Text style={tw`text-blue-600 font-medium text-sm`}>{t('admin.manage', language)}</Text>
        </TouchableOpacity>
      </View>

      {questionMetric && (
        <View style={tw`space-y-2`}>
          <QualityMetricRow
            label={t('admin.questionsTranslated', language)}
            value={`${questionMetric.approvedItems}/${questionMetric.totalItems}`}
            percentage={questionMetric.completionPercentage}
          />
          <QualityMetricRow
            label={t('admin.averageQualityScore', language)}
            value={questionMetric.avgQualityScore.toFixed(1)}
            percentage={(questionMetric.avgQualityScore / 5) * 100}
          />
          <QualityMetricRow
            label={t('admin.reviewedItems', language)}
            value={`${questionMetric.reviewedItems}/${questionMetric.totalItems}`}
            percentage={(questionMetric.reviewedItems / questionMetric.totalItems) * 100}
          />
        </View>
      )}
    </View>
  );
};

// Quality Metric Row Component
interface QualityMetricRowProps {
  label: string;
  value: string;
  percentage: number;
}

const QualityMetricRow: React.FC<QualityMetricRowProps> = ({ label, value, percentage }) => (
  <View style={tw`flex-row items-center justify-between`}>
    <Text style={tw`text-gray-600 flex-1`}>{label}</Text>
    <Text style={tw`text-gray-800 font-medium mr-2`}>{value}</Text>
    <View style={tw`w-16 h-2 bg-gray-200 rounded-full`}>
      <View
        style={[
          tw`h-2 bg-blue-500 rounded-full`,
          { width: `${Math.min(percentage, 100)}%` }
        ]}
      />
    </View>
  </View>
);

// Placeholder components for complex features
const QualityOverviewChart: React.FC<{ qualityMetrics: any }> = () => {
  const { currentLanguage } = useLanguageStore();
  const language = currentLanguage;
  return (
    <View style={tw`h-40 bg-gray-100 rounded-lg items-center justify-center`}>
      <Text style={tw`text-gray-500`}>{t('admin.qualityChartPlaceholder', language)}</Text>
    </View>
  );
};

const UsageAnalyticsChart: React.FC<{ metrics: LocalizationMetrics[] }> = () => {
  const { currentLanguage } = useLanguageStore();
  const language = currentLanguage;
  return (
    <View style={tw`h-40 bg-gray-100 rounded-lg items-center justify-center`}>
      <Text style={tw`text-gray-500`}>{t('admin.usageChartPlaceholder', language)}</Text>
    </View>
  );
};

const RecentActivityList: React.FC = () => {
  const { currentLanguage } = useLanguageStore();
  const language = currentLanguage;
  return (
    <View style={tw`space-y-2`}>
      <Text style={tw`text-gray-500 text-center py-4`}>{t('admin.recentActivityPlaceholder', language)}</Text>
    </View>
  );
};

const AnalyticsMetricCard: React.FC<{ metric: LocalizationMetrics; onViewDetails: () => void }> = ({ metric, onViewDetails }) => {
  const { currentLanguage } = useLanguageStore();
  const language = currentLanguage;
  return (
    <View style={tw`bg-white rounded-lg p-4 mb-3`}>
      <View style={tw`flex-row items-center justify-between mb-2`}>
        <Text style={tw`font-bold text-gray-800`}>{metric.language.toUpperCase()}</Text>
        <TouchableOpacity onPress={onViewDetails}>
          <Text style={tw`text-blue-600 text-sm`}>{t('admin.viewDetails', language)}</Text>
        </TouchableOpacity>
      </View>
      <View style={tw`flex-row justify-between`}>
        <Text style={tw`text-gray-600`}>{t('admin.users', language)}: {metric.totalUsers}</Text>
        <Text style={tw`text-gray-600`}>{t('admin.sessions', language)}: {metric.totalSessions}</Text>
        <Text style={tw`text-gray-600`}>{t('admin.audio', language)}: {metric.audioUsageRate.toFixed(1)}%</Text>
      </View>
    </View>
  );
};

const CommunityStatsOverview: React.FC = () => {
  const { currentLanguage } = useLanguageStore();
  const language = currentLanguage;
  return (
    <View style={tw`space-y-2`}>
      <Text style={tw`text-gray-500 text-center py-4`}>{t('admin.communityStatsPlaceholder', language)}</Text>
    </View>
  );
};

const PendingReviewsList: React.FC = () => {
  const { currentLanguage } = useLanguageStore();
  const language = currentLanguage;
  return (
    <View style={tw`space-y-2`}>
      <Text style={tw`text-gray-500 text-center py-4`}>{t('admin.pendingReviewsPlaceholder', language)}</Text>
    </View>
  );
};

const TopContributorsList: React.FC = () => {
  const { currentLanguage } = useLanguageStore();
  const language = currentLanguage;
  return (
    <View style={tw`space-y-2`}>
      <Text style={tw`text-gray-500 text-center py-4`}>{t('admin.topContributorsPlaceholder', language)}</Text>
    </View>
  );
};